import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Anchor, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatErr } from "@/lib/api";
import { useBranding } from "@/lib/branding";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const { boatName, spaName, loginLogoUrl: logoUrl, heroUrl } = useBranding();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success("Welcome back");
      // Spa Ops kiosk accounts are auto-routed to the dedicated kiosk view.
      if (user?.role === "spa_ops" || user?.role === "pantry") navigate("/spa-ops");
      else if (user?.role === "spa_door") navigate("/spa-door");
      else if (user?.role === "management_company") navigate("/mgmt");
      else navigate("/admin");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:block relative bg-primary">
        {heroUrl && <img src={heroUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-primary/50" />
        <div className="relative z-10 h-full flex flex-col justify-between p-14 text-background">
          <div className="flex items-center gap-3">
            {logoUrl ? <img src={logoUrl} alt="Logo" className="h-8 w-8 object-contain" /> : <Anchor className="h-5 w-5" strokeWidth={1.5} />}
            <span className="font-display text-lg">{spaName}</span>
          </div>
          <div>
            <div className="overline text-accent mb-4">Crew Portal</div>
            <h2 className="font-display text-5xl leading-tight max-w-md">Manage the spa,<br/>the crew,<br/>the calm.</h2>
          </div>
          <div className="overline text-background/60">{boatName} · Onboard</div>
        </div>
      </div>

      <div className="flex flex-col justify-center px-8 md:px-20 py-16">
        <Link to="/" className="overline text-primary/50 hover:text-primary transition-colors mb-16" data-testid="admin-back-home">← Guest site</Link>
        <div className="max-w-md">
          <div className="overline text-accent mb-4">Sign in</div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight mb-10">Crew access.</h1>
          <form onSubmit={submit} className="space-y-6" data-testid="admin-login-form">
            <div>
              <Label className="overline text-primary/60">Email</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required data-testid="admin-email-input"
                className="mt-2 bg-transparent border-0 border-b-2 border-primary/20 rounded-none px-0 h-12 focus-visible:ring-0 focus-visible:border-primary" />
            </div>
            <div>
              <Label className="overline text-primary/60">Password</Label>
              <Input value={password} onChange={(e) => setPassword(e.target.value)} type="password" required data-testid="admin-password-input"
                className="mt-2 bg-transparent border-0 border-b-2 border-primary/20 rounded-none px-0 h-12 focus-visible:ring-0 focus-visible:border-primary" />
            </div>
            <Button type="submit" disabled={loading} className="w-full h-14 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="admin-login-submit">
              {loading ? "Signing in…" : "Sign in"} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
